"use client";

import { useEffect, useRef, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import gsap from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollSmoother, ScrollTrigger);

export default function ScrollSmootherWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);

  // Save scroll position BEFORE route changes using useLayoutEffect
  // This runs synchronously before the DOM updates
  useLayoutEffect(() => {
    // If we're LEAVING the collection page, save scroll position
    if (prevPathnameRef.current === '/collection' && pathname !== '/collection') {
      const smoother = ScrollSmoother.get();
      const scrollPos = smoother?.scrollTop() ?? window.scrollY;
      if (scrollPos > 0) {
        sessionStorage.setItem('collectionScrollPos', String(scrollPos));
      }
    }
  }, [pathname]);

  useEffect(() => {
    // Capture previous pathname IMMEDIATELY into a local variable
    // This preserves the value for use in setTimeout callbacks
    const previousPathname = prevPathnameRef.current;

    // Kill all existing ScrollTriggers and ScrollSmoother when route changes
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    const currentSmoother = ScrollSmoother.get();
    
    if (currentSmoother) {
      currentSmoother.kill();
    }

    // Reset scroll position immediately (multiple methods for reliability)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Delay to ensure DOM is ready and prevent race conditions
    const timer = setTimeout(() => {
      const isMobile = window.innerWidth < 768;
      
      // Don't create ScrollSmoother on mobile - native scroll works better
      // and avoids conflicts with pinned ScrollTrigger animations
      if (isMobile) {
        // Handle scroll position for collection page without ScrollSmoother
        if (pathname === '/collection') {
          const savedPos = sessionStorage.getItem('collectionScrollPos');
          const cameFromMicDetail = previousPathname?.startsWith('/collection/') && 
                                     previousPathname !== '/collection';
          
          if (savedPos && cameFromMicDetail) {
            setTimeout(() => {
              window.scrollTo(0, parseFloat(savedPos));
            }, 50);
          } else {
            sessionStorage.removeItem('collectionScrollPos');
          }
        }
        
        // Refresh ScrollTrigger for pinned elements
        setTimeout(() => {
          ScrollTrigger.refresh();
        }, 100);
        
        // Update previous pathname and exit early
        prevPathnameRef.current = pathname;
        return;
      }
      
      // Desktop only: Create ScrollSmoother
      const smoother = ScrollSmoother.create({
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        smooth: 1.5,
        effects: true,
      });

      // Handle scroll position based on navigation context
      if (pathname === '/collection') {
        const savedPos = sessionStorage.getItem('collectionScrollPos');
        const cameFromMicDetail = previousPathname?.startsWith('/collection/') && 
                                   previousPathname !== '/collection';

        if (savedPos && cameFromMicDetail) {
          // Restore scroll position when coming back from microphone detail
          setTimeout(() => {
            smoother.scrollTo(parseFloat(savedPos), false);
          }, 50);
        } else {
          // Reset to top when coming from anywhere else
          smoother.scrollTo(0, false);
          sessionStorage.removeItem('collectionScrollPos');
        }
      } else {
        // Not on collection page - reset to top
        smoother.scrollTo(0, false);
      }

      // Refresh ScrollTrigger after creation with additional delay
      const refreshTimer = setTimeout(() => {
        ScrollTrigger.refresh();
        // Only reset scroll again if NOT restoring collection position
        if (pathname !== '/collection') {
          const currentSmoother = ScrollSmoother.get();
          if (currentSmoother) {
            currentSmoother.scrollTo(0, false);
          }
        }
      }, 100);

      return () => clearTimeout(refreshTimer);
    }, 100);

    // Update previous pathname for next navigation (at the END)
    prevPathnameRef.current = pathname;

    return () => {
      clearTimeout(timer);
      // Kill all ScrollTriggers on unmount
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      const smoother = ScrollSmoother.get();
      if (smoother) {
        smoother.kill();
      }
    };
  }, [pathname]);

  return (
    <div id="smooth-wrapper">
      <div id="smooth-content">{children}</div>
    </div>
  );
}
